// Select Deliverables to Track Page
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatSoldAs } from '../../components/sales/saleConstants';

// All available horse deliverable types
const allHorseDeliverables = [
  { key: 'breeding_soundness_exam', label: 'Breeding Soundness Exam', sexDependent: 'male' },
  { key: 'catalogue_page', label: 'Catalogue Page', required: true },
  { key: 'coggins', label: 'Coggins', required: true },
  { key: 'entry_fee', label: 'Entry Fee', required: true },
  { key: 'eva', label: 'EVA', required: true },
  { key: 'eye_certificate', label: 'Eye Certificate' },
  { key: 'fetal_sexing', label: 'Fetal Sexing', sexDependent: 'pregnant_mare' },
  { key: 'health_certificate', label: 'Health Certificate', required: true },
  { key: 'image', label: 'Image' },
  { key: 'jockey_club_papers', label: 'Jockey Club Papers', required: true },
  { key: 'medication_treatment_disclosure', label: 'Medication & Treatment Disclosure' },
  { key: 'pedigree_proof', label: 'Pedigree Proof' },
  { key: 'questionnaire', label: 'Questionnaire' },
  { key: 'scope', label: 'Scope' },
  { key: 'stallion_service_certificate', label: 'Stallion Service Certificate', sexDependent: 'male' },
  { key: 'surgery_letter', label: 'Surgery Letter' },
  { key: 'vet_report', label: 'Vet Report' },
  { key: 'video', label: 'Video' },
  { key: 'xrays', label: 'Xrays' },
  { key: 'other', label: 'Other' },
];

// Owner deliverable types
const allOwnerDeliverables = [
  { key: 'sale_authorization', label: 'Sale Authorization', required: true },
  { key: 'consignment_contract', label: 'Consignment Contract', required: true },
  { key: 'hip_number_email', label: 'Hip Number Email' },
  { key: 'ship_in_note_email', label: 'Ship-in Note Email' },
];

const SelectDeliverablesPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [deliverableCounts, setDeliverableCounts] = useState({});

  const { data, isLoading: loading } = useQuery({
    queryKey: QK.selectDeliverables(id),
    queryFn: async () => {
      const oppRes = await api.get(`/sale-opportunities/${id}`);
      const opp = oppRes.data;
      const [horseRes, saleRes] = await Promise.all([
        opp.horse_id ? api.get(`/horses/${opp.horse_id}`) : Promise.resolve({ data: null }),
        opp.sale_id  ? api.get(`/sales/${opp.sale_id}`)   : Promise.resolve({ data: null }),
      ]);
      return { opportunity: opp, horse: horseRes.data, sale: saleRes.data };
    },
    enabled: !!id,
    onError: () => { toast.error('Failed to load opportunity'); navigate('/sales/opportunities'); },
  });
  const opportunity = data?.opportunity ?? null;
  const horse       = data?.horse       ?? null;
  const sale        = data?.sale        ?? null;

  // Initialise deliverable counts from opportunity data
  useEffect(() => {
    if (!opportunity) return;
    const existingDeliverables = opportunity.deliverables || [];
    const counts = {};
    existingDeliverables.forEach(d => {
      if (d.deliverable_type_horse) {
        counts[d.deliverable_type_horse] = (counts[d.deliverable_type_horse] || 0) + 1;
      }
    });
    allHorseDeliverables.forEach(del => {
      if (del.required && !counts[del.key]) counts[del.key] = 1;
    });
    setDeliverableCounts(counts);
  }, [opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const incrementCount = (key) => {
    setDeliverableCounts(prev => ({
      ...prev,
      [key]: (prev[key] || 0) + 1
    }));
  };

  const decrementCount = (key, isRequired) => {
    if (isRequired && deliverableCounts[key] <= 1) return; // Don't allow removing required
    
    setDeliverableCounts(prev => {
      const newCount = (prev[key] || 0) - 1;
      if (newCount <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: newCount };
    });
  };

  const selectAllHorse = () => {
    const allCounts = {};
    allHorseDeliverables.forEach(d => {
      allCounts[d.key] = deliverableCounts[d.key] || 1;
    });
    setDeliverableCounts(allCounts);
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // deleteConfirm = { type, label, candidates: [{id, nickname, status}], selected: id|null, onConfirm }

  const handleSave = async () => {
    setSaving(true);
    try {
      // Existing deliverables keyed by type
      const existingDeliverables = opportunity.deliverables || [];
      const existingByType = {};
      existingDeliverables.forEach(d => {
        const t = d.deliverable_type_horse;
        if (t) { if (!existingByType[t]) existingByType[t] = []; existingByType[t].push(d); }
      });

      const allTypes = new Set([...Object.keys(deliverableCounts), ...Object.keys(existingByType)]);
      const creates = [];
      const deletePending = []; // types that need user to pick which to delete
      const directDeletes = []; // single-instance deletes needing no prompt

      for (const type of allTypes) {
        const desired = deliverableCounts[type] || 0;
        const existing = existingByType[type] || [];
        const diff = desired - existing.length;
        if (diff > 0) {
          for (let i = 0; i < diff; i++) creates.push(type);
        } else if (diff < 0) {
          // Need to ask which one(s) to delete — only if there are multiple of this type
          const res = await api.get(`/sale-deliverables?sale_opportunity_id=${id}&type=${encodeURIComponent(type)}`);
          const candidates = (res.data || []).map(d => ({
            id: d.id,
            nickname: d.nickname || `${allHorseDeliverables.find(h => h.key === type)?.label || type} (no nickname)`,
            status: d.status || 'not_started',
          }));
          if (candidates.length > 1) {
            // Multiple — ask user which to delete
            deletePending.push({ type, label: allHorseDeliverables.find(h => h.key === type)?.label || type, candidates, toDelete: Math.abs(diff) });
          } else {
            // Only one — delete it directly without prompting
            candidates.slice(0, Math.abs(diff)).forEach(c => directDeletes.push(c.id));
          }
        }
      }

      // If any deletions needed, prompt one at a time
      if (deletePending.length > 0) {
        setSaving(false);
        // Process deletion prompts sequentially
        const promptNext = (queue, completedDeletes) => {
          if (queue.length === 0) {
            // All deletions confirmed — now execute creates + deletes
            executeSave(creates, [...directDeletes, ...completedDeletes]);
            return;
          }
          const current = queue[0];
          setDeleteConfirm({
            type: current.type,
            label: current.label,
            candidates: current.candidates,
            toDelete: current.toDelete,
            selected: [],
            onConfirm: (selectedIds) => {
              setDeleteConfirm(null);
              promptNext(queue.slice(1), [...completedDeletes, ...selectedIds]);
            },
          });
        };
        promptNext(deletePending, []);
        return;
      }

      await executeSave(creates, directDeletes);
    } catch (err) {
      toast.error('Failed to save deliverables');
      setSaving(false);
    }
  };

  const executeSave = async (creates, deleteIds) => {
    setSaving(true);
    try {
      const promises = [
        ...creates.map(type => api.post('/sale-deliverables', { sale_opportunity_id: id, deliverable_type_horse: type, status: 'not_started' })),
        ...deleteIds.map(did => api.delete(`/sale-deliverables/${did}`)),
      ];
      await Promise.all(promises);
      toast.success('Deliverables updated');
      navigate(`/sales/opportunities/${id}`);
    } catch {
      toast.error('Failed to save deliverables');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <SidebarLayout><div>Loading...</div></SidebarLayout>;
  }

  return (
    <>
    <SidebarLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold">Select Deliverables to Track</h1>
          <p className="text-muted-foreground mt-1">
            Choose which deliverables you need to track for this sale entry
          </p>
        </div>

        {/* Sale Entry Details */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Sale Entry Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Sale</span>
                <p className="font-medium">{opportunity?.opportunity_name?.split(' - ')[0] || sale?.sale_name || '-'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Horse</span>
                <p className="font-medium">{horse?.system_name || horse?.name || '-'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">To Be Sold As</span>
                <p className="font-medium">{formatSoldAs(opportunity?.sold_as)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Entry Status</span>
                <p className="font-medium">{opportunity?.entry_status ? opportunity.entry_status.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Horse Deliverables */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-serif text-lg">Horse Deliverables</CardTitle>
            <Button variant="outline" size="sm" onClick={selectAllHorse}>
              SELECT ALL
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium mb-4">Current Document Types:</p>
            <div className="space-y-2">
              {allHorseDeliverables.map((deliverable) => {
                const count = deliverableCounts[deliverable.key] || 0;
                const isEnabled = count > 0;
                
                return (
                  <div key={deliverable.key} className="flex items-center justify-between p-2 border-b border-border">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={deliverable.key}
                        checked={isEnabled}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            incrementCount(deliverable.key);
                          } else if (!deliverable.required) {
                            setDeliverableCounts(prev => {
                              const { [deliverable.key]: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        disabled={deliverable.required}
                      />
                      <Label
                        htmlFor={deliverable.key}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {deliverable.label}
                        {deliverable.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                    </div>
                    {isEnabled && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => decrementCount(deliverable.key, deliverable.required)}
                          disabled={deliverable.required && count <= 1}
                          className="h-8 w-8 p-0"
                        >
                          -
                        </Button>
                        <div className="w-10 h-8 border border-input flex items-center justify-center bg-background">
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => incrementCount(deliverable.key)}
                          className="h-8 w-8 p-0"
                        >
                          +
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate(`/sales/opportunities/${id}`)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {saving ? 'Saving...' : 'Save & View Deliverables Tracker'}
          </Button>
        </div>
      </div>
    </SidebarLayout>

    {/* Delete confirmation dialog */}
    {deleteConfirm && (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Which "{deleteConfirm.label}" should be removed?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">
            Select {deleteConfirm.toDelete} deliverable{deleteConfirm.toDelete > 1 ? 's' : ''} to remove:
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {deleteConfirm.candidates.map(c => (
              <label key={c.id} className={`flex items-center gap-3 p-2 rounded border cursor-pointer hover:bg-muted/40 ${(deleteConfirm.selected || []).includes(c.id) ? 'bg-muted border-primary' : ''}`}>
                <input
                  type="checkbox"
                  checked={(deleteConfirm.selected || []).includes(c.id)}
                  onChange={() => {
                    const sel = deleteConfirm.selected || [];
                    const next = sel.includes(c.id) ? sel.filter(s => s !== c.id) : [...sel, c.id];
                    setDeleteConfirm(prev => ({ ...prev, selected: next }));
                  }}
                  className="accent-primary"
                  disabled={(deleteConfirm.selected || []).length >= deleteConfirm.toDelete && !(deleteConfirm.selected || []).includes(c.id)}
                />
                <div>
                  <p className="text-sm font-medium">{c.nickname}</p>
                  <p className="text-xs text-muted-foreground capitalize">{c.status?.replace(/_/g, ' ')}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setSaving(false); }}>Cancel</Button>
            <Button
              className="bg-destructive hover:bg-destructive/90 text-white"
              disabled={(deleteConfirm.selected || []).length !== deleteConfirm.toDelete}
              onClick={() => deleteConfirm.onConfirm(deleteConfirm.selected || [])}
            >
              Remove Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
    );
};

export default SelectDeliverablesPage;
